import React, { createRef, Component, type ComponentType, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
// TODO: JFP-2824 | Suppressed to enable upgrade to ESLint v9 - please fix this if you can!
// eslint-disable-next-line jira/restricted/@atlaskit+analytics-next
import { type UIAnalyticsEvent, AnalyticsContext } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Grid, GridColumn } from '@atlaskit/page';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import EmbeddedProductUpdater from '@atlassian/jira-analytics-providers/src/common/utils/embedded-product/view.tsx';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import FieldCopyText from '@atlassian/jira-common-components-field-copy-text/src/view.tsx';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import ExternalLinkButton from '@atlassian/jira-packages-components-external-link-button/src/view.tsx';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import ConfluenceArticle from '@atlassian/jira-servicedesk-knowledge-common/src/ui/confluence-article-iframe/index.tsx';
import type { Article } from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { fg } from '@atlassian/jira-feature-gating';
import { DraftArticleSuccessFlags } from '../common/draft-article-flags/view.tsx';
import commonMessages from '../common/i18n/messages.tsx';
import PageHeader from '../common/page-header/view.tsx';
import type { ConnectedKnowledgeHubArticleCategoryListProps } from '../knowledge-hub-article-category-list/index.tsx';
import type { KnowledgeHubArticleLabelListProps } from '../knowledge-hub-article-label-list/index.tsx';
import { ConfluenceEmbeddedViewWrapper } from '../knowledge-hub-embedded-confluence/view.tsx';
import messages from './messages.tsx';

export type KnowledgeHubArticleProps = {
	isServer: boolean | undefined;
	canViewSpaceUrl: boolean;
	article: Article | undefined;
	projectKey: string;
	KnowledgeHubArticleCategoryList: ComponentType<ConnectedKnowledgeHubArticleCategoryListProps>;
	KnowledgeHubArticleLabelList: ComponentType<KnowledgeHubArticleLabelListProps>;
	iframeLoading: boolean;
	spaceKey: string | undefined;
	articleId: string;
	onEditClicked: // eslint-disable-next-line @typescript-eslint/no-explicit-any
	((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
	onCopyClicked: // eslint-disable-next-line @typescript-eslint/no-explicit-any
	((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
	onIFrameLoaded: () => void;
	intl: IntlShape;
	isJcs?: boolean;
	helpCenterId?: string;
};

// eslint-disable-next-line jira/react/no-class-components
export class KnowledgeHubArticleStateless extends Component<KnowledgeHubArticleProps> {
	static defaultProps = {
		onEditClicked: undefined,
		onCopyClicked: undefined,
	};

	breadcrumbsRef = createRef<HTMLDivElement>();

	getCategoryIds() {
		if (!this.props.article) {
			return '';
		}
		const categoryIds =
			// @ts-expect-error - TS2304 - Cannot find name 'Category'.
			this.props.article.categories?.map((category: Category) => category.id).join(',') || '';
		return categoryIds;
	}

	render() {
		const {
			intl,
			onCopyClicked,
			onEditClicked,
			KnowledgeHubArticleCategoryList,
			KnowledgeHubArticleLabelList,
			onIFrameLoaded,
			iframeLoading,
			isServer,
			projectKey,
			article,
			canViewSpaceUrl,
			spaceKey: propSpaceKey,
			articleId,
			isJcs,
			helpCenterId,
		} = this.props;

		const {
			viewUrl,
			shareUrl,
			editUrl,
			title,
			categories,
			id,
			spaceKey: apiSpaceKey,
			spaceName,
			spaceUrl,
			spaceId,
			categoriesSupported,
			isBusy,
			labelState,
			isEditable,
		} = article || {};
		const isConfluenceServerArticle = isServer;
		const showConfluenceEditLink = isConfluenceServerArticle;
		const hideKnowledgeHubArticleCategoryList =
			isJcs && fg('enable_articles_page_csm') ? true : categoriesSupported === false;
		const spaceKey = apiSpaceKey || propSpaceKey;
		if (!iframeLoading) {
			GlobalPageLoadExperience.markFMP();
		}

		return (
			<AnalyticsContext
				data={{
					source: `knowledgeArticle${SCREEN}`,
					attributes: {
						'Category Ids': this.getCategoryIds(),
						source: 'KB Hub',
						isConfluenceServer: isServer,
						spaceId,
					},
				}}
			>
				<FullPageWidthContainer>
					{article && <ViewTracker />}
					<EmbeddedProductUpdater embeddedProduct="confluence" />

					{isJcs && fg('enable_articles_page_csm') ? null : (
						<div ref={this.breadcrumbsRef}>
							<Grid layout="fluid">
								<GridColumn>
									{article && (
										<PageHeader showDefaultTitle={false}>
											{!isConfluenceServerArticle ? null : title}
										</PageHeader>
									)}
								</GridColumn>
							</Grid>
						</div>
					)}
					<GridContainerWithStickyLastGridColumn>
						<Grid layout="fluid">
							<GridColumn medium={8}>
								{article || spaceKey ? (
									<>
										{!isConfluenceServerArticle ? (
											<ConfluenceEmbeddedViewWrapper
												spaceKey={spaceKey}
												pageId={articleId}
												breadcrumbsRef={this.breadcrumbsRef}
												projectKey={projectKey}
												spaceId={spaceId}
												onIFrameLoaded={onIFrameLoaded}
												isJcs={isJcs}
												helpCenterId={helpCenterId}
											/>
										) : (
											<StyledConfluenceArticle
												// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
												src={viewUrl}
												onIFrameLoaded={onIFrameLoaded}
											/>
										)}
									</>
								) : null}
							</GridColumn>

							<GridColumn medium={4}>
								{article && (
									<>
										<Aside>
											{showConfluenceEditLink && (
												<AsideSection>
													<ExternalLinkButton
														// @ts-expect-error - TS2769 - No overload matches this call.
														href={editUrl}
														onClick={onEditClicked}
													>
														{intl.formatMessage(commonMessages.editInConfluence)}
													</ExternalLinkButton>
												</AsideSection>
											)}
											<AsideSection>
												<Heading size="small">{intl.formatMessage(messages.shareArticle)}</Heading>
												<StyledFieldCopyText
													shouldFitContainer
													label={intl.formatMessage(messages.shareableUrlLabel)}
													tooltipLabel={intl.formatMessage(messages.copyArticleUrlLabel)}
													// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
													value={shareUrl}
													onCopyClick={onCopyClicked}
												/>
											</AsideSection>
											{spaceName && (
												<AsideSection>
													<Heading size="small">
														{intl.formatMessage(messages.belongsToThisSpace)}
													</Heading>
													{canViewSpaceUrl ? (
														<StyledSpaceInfo>
															<Tooltip content={intl.formatMessage(messages.tooltipText)}>
																<Button
																	href={spaceUrl}
																	target="_blank"
																	appearance="link"
																	rel="noopener noreferrer nofollow"
																	spacing="none"
																>
																	{spaceName}
																</Button>
															</Tooltip>
														</StyledSpaceInfo>
													) : (
														<StyledSpaceInfo>{spaceName}</StyledSpaceInfo>
													)}
												</AsideSection>
											)}
											{categories && !hideKnowledgeHubArticleCategoryList && (
												<AsideSection>
													<KnowledgeHubArticleCategoryList
														// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
														articleId={id}
														currentCategories={categories}
														isUpdatingCategories={isBusy}
														isEditable={!!isEditable}
													/>
												</AsideSection>
											)}
											{!isConfluenceServerArticle && labelState && (
												<AsideSection>
													<KnowledgeHubArticleLabelList
														isEditable={!!isEditable}
														labelState={labelState}
														projectKey={projectKey || ''}
														// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
														articleId={id}
														spaceKey={spaceKey || ''}
													/>
												</AsideSection>
											)}
										</Aside>
									</>
								)}
							</GridColumn>
						</Grid>
					</GridContainerWithStickyLastGridColumn>
					{!iframeLoading && <JsmPageInteractiveEvent />}
					<DraftArticleSuccessFlags />
				</FullPageWidthContainer>
			</AnalyticsContext>
		);
	}
}

export default injectIntl(KnowledgeHubArticleStateless);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Aside = styled.aside({
	marginLeft: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AsideSection = styled.div({
	paddingBottom: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullPageWidthContainer = styled.div({
	maxWidth: '1376px',
	margin: '0 auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GridContainerWithStickyLastGridColumn = styled.div({
	/* !! HACK ALERT !! */
	/* Until we can pass through a className, this will have to do. */
	/* Go down one div, then target the last child. */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> div > :last-child': {
		position: 'sticky',
		top: token('space.400'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledSpaceInfo = styled.div({
	padding: 0,
	margin: 0,
	paddingTop: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledFieldCopyText = styled(FieldCopyText)({
	marginTop: token('space.negative.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledConfluenceArticle = styled(ConfluenceArticle)({
	marginBottom: token('space.200'),
});
